// Shared model for pending-invoice exports (Excel + PDF).
// Keeps grouping, service labels and money math in ONE place so both exports match.

// ── Period labels ────────────────────────────────────────────────────────────
const HY_PERIOD = { '15 Days': '15 օրով', '30 Days': '30 օրով', '90 Days': '90 օրով', '1 Year': '1 տարով' };
const RU_PERIOD = { '15 Days': '15 дней', '30 Days': '30 дней', '90 Days': '90 дней', '1 Year': '1 год' };

// ── Service labels (AMD → Armenian, GEL → Russian) ───────────────────────────
export function labelHy(serviceType, period) {
  switch (serviceType) {
    case 'transitCard':      return 'Տարանցիկ քարտ Վրաստանի տարածքում';
    case 'parking_80':       return 'Կայանատեղի Վրաստանի տարածքում';
    case 'parking_40':       return 'Կայանատեղի Վրաստանի տարածքում (40)';
    case 'truckInsurance':   return 'Բեռնատարի ապահովագրություն Վրաստանի տարածքում ' + (HY_PERIOD[period] || period || '');
    case 'trailerInsurance': return 'Կիսակցորդի ապահովագրություն Վրաստանի տարածքում ' + (HY_PERIOD[period] || period || '');
    case 'russiaInsurance':  return 'Բեռնատարի ապահովագրություն Ռուսաստանի տարածքում 1 տարով';
    default:                 return 'Այլ';
  }
}

export function labelRu(serviceType, period) {
  switch (serviceType) {
    case 'transitCard':      return 'Транзитная карта';
    case 'parking_80':       return 'Оплата за стоянку';
    case 'parking_40':       return 'Оплата за стоянку (40)';
    case 'truckInsurance':   return 'Страховка грузовика на ' + (RU_PERIOD[period] || period || '');
    case 'trailerInsurance': return 'Страховка прицепа на ' + (RU_PERIOD[period] || period || '');
    case 'russiaInsurance':  return 'Страховка грузовика в России на 1 год';
    default:                 return 'Прочее';
  }
}

export function round2(n) {
  return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}

// Aggregate invoices by (service_type + insurance_period).
// Net value comes straight from the invoice: total_amountGel × exchange_rate
// (for GEL / tts_georgia there is no conversion/VAT — value is total_amountGel).
function groupByService(invoices, isGel) {
  const map = {};
  invoices.forEach((inv) => {
    const key = `${inv.service_type}|${inv.insurance_period || ''}`;
    if (!map[key]) {
      map[key] = { service_type: inv.service_type, period: inv.insurance_period || '', qty: 0, valueNoVat: 0 };
    }
    const g = map[key];
    g.qty += Number(inv.quantity) || 0;
    const gel = Number(inv.total_amountGel) || 0;
    g.valueNoVat += isGel ? gel : gel * (Number(inv.exchange_rate) || 0);
  });
  return Object.values(map);
}

/**
 * Build the shared export model for one company's pending invoices.
 * Returns null if there is nothing to export.
 *
 * rows: [{ label, qty, unit, value, vat, total }]  (vat is null for GEL)
 */
export function buildPendingModel(company, invoices) {
  if (!invoices || invoices.length === 0) return null;

  const isGel = !!invoices[0].tts_georgia;
  const inv0 = invoices[0];

  // Header title: AMD → Armenian, GEL → Russian; fall back to EN / username.
  const localized = isGel
    ? (inv0.company_name_ru || inv0.company_name_en)
    : (inv0.company_name_hy || inv0.company_name_en);
  const title = localized || company || inv0.company || 'Company';

  // File name is always English.
  const englishName = inv0.company_name_en || company || inv0.company || 'Company';
  const fileBase = String(englishName).replace(/[\\/?*[\]:<>"|]/g, '').trim().slice(0, 80) || 'Company';

  const today = new Date().toISOString().slice(0, 10);

  const rows = groupByService(invoices, isGel).map((g) => {
    const unit = g.qty ? round2(g.valueNoVat / g.qty) : 0;
    const value = round2(g.valueNoVat);
    const vat = isGel ? null : round2(g.valueNoVat * 0.2);
    const total = isGel ? value : round2(g.valueNoVat * 1.2);
    const label = isGel ? labelRu(g.service_type, g.period) : labelHy(g.service_type, g.period);
    return { label, qty: round2(g.qty), unit, value, vat, total };
  });

  const sumTotal = round2(rows.reduce((acc, r) => acc + r.total, 0));
  const headers = isGel
    ? ['Количество', 'Цена за ед', 'Сумма']
    : ['Քանակ', 'Միավորի գին', 'Արժեք', 'ԱԱՀ', 'Ընդհանուր'];

  return { isGel, title, fileBase, today, rows, sumTotal, headers };
}
